import { Link } from "react-router-dom";
import {
  MdBusinessCenter,
  MdOutlineMail,
  MdPlace,
  MdArrowOutward,
} from "react-icons/md";
import {
  FaPhoneAlt,
  FaFacebook,
  FaInstagram,
  FaLinkedin,
  FaTiktok,
} from "react-icons/fa";
import { Helmet } from "react-helmet-async";
import logo from "../../assets/new_logo.webp";

const footerLinkClass =
  "font-customFont1 font-semibold transition-colors duration-200 hover:!text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:hover:!text-primary dark:focus-visible:ring-offset-[#111114]";

const footerMutedTextClass =
  "font-customFont1 text-sm leading-relaxed !text-[#4B5563] dark:!text-[#D1D5DB]";

const trackFooterEvent = (eventName, params = {}) => {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", eventName, {
      sijainti: "footer",
      ...params,
    });
  }
};

const scrollTop = () => {
  window.scrollTo({ top: 0, behavior: "auto" });
};

const socialLinks = [
  {
    name: "Facebook",
    href: "https://www.facebook.com/spiderlegsproductions/",
    icon: FaFacebook,
    eventChannel: "facebook",
  },
  {
    name: "Instagram",
    href: "https://www.instagram.com/spiderlegsproductions/",
    icon: FaInstagram,
    eventChannel: "instagram",
  },
  {
    name: "LinkedIn",
    href: "https://www.linkedin.com/in/spiderlegs-productions/",
    icon: FaLinkedin,
    eventChannel: "linkedin",
  },
  {
    name: "TikTok",
    href: "https://www.tiktok.com/@spiderlegsproductions",
    icon: FaTiktok,
    eventChannel: "tiktok",
  },
];

const navLinks = [
  { label: "Koti", to: "/", key: "koti" },
  { label: "Vuokrattavat", to: "/vuokrattavat", key: "vuokrattavat" },
  { label: "Palvelut", to: "/palvelut", key: "palvelut" },
  { label: "Meistä", to: "/meista", key: "meista" },
  { label: "UKK", to: "/UKK", key: "ukk" },
  { label: "Tee varaus", to: "/tee-varaus", key: "tee_varaus" },
];

export default function Footer() {
  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "@id": "https://spiderlegsproductions.com/#organization-footer",
            name: "Spiderlegs Productions",
            url: "https://spiderlegsproductions.com/",
            logo: "https://spiderlegsproductions.com/assets/new_logo.webp",
            email: "info@spiderlegsproductions.com",
            telephone: "+358443519265",
            address: {
              "@type": "PostalAddress",
              addressLocality: "Kerava",
              addressRegion: "Uusimaa",
              addressCountry: "FI",
            },
            sameAs: [
              "https://www.facebook.com/spiderlegsproductions/",
              "https://www.instagram.com/spiderlegsproductions/",
              "https://www.linkedin.com/in/spiderlegs-productions/",
              "https://www.tiktok.com/@spiderlegsproductions",
            ],
          })}
        </script>
      </Helmet>

      <footer
        className="
          relative overflow-hidden border-t border-[#DDE7E5]
          bg-gradient-to-br from-white via-[#F3FFFC] to-[#E9FBF7]
          font-customFont1 !text-[#111827]
          dark:border-darkborder
          dark:from-[#050505] dark:via-[#08080A] dark:to-[#0D0D12]
          dark:!text-white
        "
        aria-label="Alatunniste"
      >
        <div
          aria-hidden="true"
          className="
            pointer-events-none absolute left-1/2 top-0 h-64 w-64
            -translate-x-1/2 rounded-full bg-primary/15 blur-3xl
            dark:bg-primary/10
          "
        />

        <div className="relative mx-auto max-w-7xl px-4 py-16">
          <div
            className="
              rounded-[2rem] border border-[#DDE7E5] bg-white/90 p-6 shadow-xl backdrop-blur-xl
              dark:border-darkborder dark:bg-[#111114]/95
              md:p-8
            "
          >
            {/* Yläosa */}
            <div
              className="
                flex flex-col gap-8 border-b border-[#E5E7EB] pb-8
                dark:border-white/10
                lg:flex-row lg:items-center lg:justify-between
              "
            >
              <div className="flex max-w-2xl flex-col items-center gap-4 text-center sm:flex-row sm:items-start sm:text-left">
                <div
                  className="
                    flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl
                    border border-[#DDE7E5] bg-[#F8FAFC]
                    dark:border-darkborder dark:bg-darksurface
                  "
                >
                  <img
                    src={logo}
                    alt="Spiderlegs Productions logo"
                    loading="lazy"
                    className="h-11 w-11 object-contain"
                  />
                </div>

                <div>
                  <span
                    className="
                      mb-2 inline-flex rounded-full border border-primary/40 bg-primary/10
                      px-4 py-1.5 font-customFont1 text-xs font-extrabold uppercase
                      tracking-[0.14em] !text-[#111827] shadow-sm
                      dark:!text-white
                    "
                  >
                    Spiderlegs Productions
                  </span>

                  <p className={footerMutedTextClass}>
                    Tapahtumatekniikan, äänentoiston, valotekniikan ja
                    laitevuokrauksen palvelu Keravalta.
                  </p>
                </div>
              </div>

              <div className="flex flex-col items-center gap-4">
                <p className="font-customFont1 text-sm font-bold uppercase tracking-[0.12em] !text-[#111827] dark:!text-white">
                  Seuraa somessa
                </p>

                <div className="flex gap-3">
                  {socialLinks.map((social) => {
                    const Icon = social.icon;

                    return (
                      <a
                        key={social.name}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Spiderlegs Productions ${social.name}`}
                        title={`Spiderlegs Productions ${social.name}`}
                        onClick={() =>
                          trackFooterEvent("some_klikattu", {
                            kanava: social.eventChannel,
                          })
                        }
                        className="
                          flex h-11 w-11 items-center justify-center rounded-full
                          border border-[#DDE7E5] bg-[#F8FAFC]
                          !text-[#111827] shadow-sm
                          transition duration-200
                          hover:-translate-y-0.5 hover:border-primary hover:bg-primary hover:!text-[#111827] hover:shadow-md
                          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-white
                          dark:border-darkborder dark:bg-darksurface dark:!text-white
                          dark:hover:border-primary dark:hover:bg-primary dark:hover:!text-[#111827]
                          dark:focus-visible:ring-offset-[#111114]
                        "
                      >
                        <Icon size={20} />
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Keskiosa */}
            <div
              className="
                grid grid-cols-1 gap-10 py-10
                md:grid-cols-2 lg:grid-cols-4
              "
            >
              {/* Yritys */}
              <section>
                <h2 className="mb-5 font-customFont2 text-3xl font-bold uppercase leading-none !text-[#111827] dark:!text-white">
                  Yritys
                </h2>

                <div className="space-y-3">
                  <p className="flex items-start gap-3">
                    <MdPlace className="mt-1 shrink-0 text-primary" size={20} />
                    <span className={footerMutedTextClass}>
                      Kerava, Savio, FI
                    </span>
                  </p>

                  <p className="flex items-start gap-3">
                    <MdOutlineMail
                      className="mt-1 shrink-0 text-primary"
                      size={20}
                    />
                    <a
                      href="mailto:info@spiderlegsproductions.com"
                      className={`${footerLinkClass} break-all !text-[#111827] dark:!text-white`}
                      aria-label="Lähetä sähköposti Spiderlegs Productionsille"
                      onClick={() =>
                        trackFooterEvent("email_klikattu", {
                          kohde: "info@spiderlegsproductions.com",
                        })
                      }
                    >
                      info@spiderlegsproductions.com
                    </a>
                  </p>

                  <p className="flex items-start gap-3">
                    <FaPhoneAlt
                      className="mt-1 shrink-0 text-primary"
                      size={17}
                    />
                    <a
                      href="tel:+358443519265"
                      className={`${footerLinkClass} !text-[#111827] dark:!text-white`}
                      aria-label="Soita Spiderlegs Productionsille"
                      onClick={() =>
                        trackFooterEvent("puhelin_klikattu", {
                          numero: "+358443519265",
                        })
                      }
                    >
                      +358 44 351 92 65
                    </a>
                  </p>

                  <p className="flex items-start gap-3">
                    <MdBusinessCenter
                      className="mt-1 shrink-0 text-primary"
                      size={20}
                    />
                    <span className={footerMutedTextClass}>
                      Y-tunnus 3214378-6
                    </span>
                  </p>
                </div>
              </section>

              {/* Navigaatio */}
              <nav aria-label="Footer navigaatio">
                <h2 className="mb-5 font-customFont2 text-3xl font-bold uppercase leading-none !text-[#111827] dark:!text-white">
                  Sivut
                </h2>

                <ul className="space-y-3">
                  {navLinks.map((item) => (
                    <li key={item.key}>
                      <Link
                        to={item.to}
                        onClick={() => {
                          trackFooterEvent("navigation_klikattu", {
                            kohde: item.key,
                          });
                          scrollTop();
                        }}
                        className={`${footerLinkClass} !text-[#111827] dark:!text-white`}
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>

              {/* Ehdot */}
              <section>
                <h2 className="mb-5 font-customFont2 text-3xl font-bold uppercase leading-none !text-[#111827] dark:!text-white">
                  Ehdot
                </h2>

                <ul className="space-y-3">
                  <li>
                    <Link
                      to="/tietosuojalausunto"
                      onClick={() => {
                        trackFooterEvent("ehto_klikattu", {
                          kohde: "tietosuojalausunto",
                        });
                        scrollTop();
                      }}
                      className={`${footerLinkClass} !text-[#111827] dark:!text-white`}
                    >
                      Tietosuojalausunto
                    </Link>
                  </li>

                  <li>
                    <Link
                      to="/vuokrausehdot"
                      onClick={() => {
                        trackFooterEvent("ehto_klikattu", {
                          kohde: "vuokrausehdot",
                        });
                        scrollTop();
                      }}
                      className={`${footerLinkClass} !text-[#111827] dark:!text-white`}
                    >
                      Vuokrausehdot
                    </Link>
                  </li>

                  <li>
                    <button
                      type="button"
                      onClick={() => {
                        if (typeof window.openCookieSettings === "function") {
                          window.openCookieSettings();
                        } else {
                          window.dispatchEvent(
                            new CustomEvent("openCookieSettings")
                          );
                        }

                        trackFooterEvent("evasteasetukset_avattu");
                      }}
                      className={`${footerLinkClass} text-left !text-[#111827] dark:!text-white`}
                    >
                      Muuta evästeasetuksia
                    </button>
                  </li>
                </ul>
              </section>

              {/* Buukkeri */}
              <section
                className="
                  rounded-3xl border border-primary/30 bg-primary/10 p-5 shadow-sm
                  dark:border-primary/30 dark:bg-primary/5 dark:shadow-none
                "
              >
                <span
                  className="
                    mb-3 inline-flex rounded-full border border-primary/40 bg-white
                    px-3 py-1 font-customFont1 text-xs font-extrabold uppercase
                    tracking-[0.12em] !text-[#111827]
                    dark:bg-darksurface dark:!text-white
                  "
                >
                  Erillinen palvelu
                </span>

                <h2 className="mb-3 font-customFont2 text-3xl font-bold uppercase leading-none !text-[#111827] dark:!text-white">
                  Buukkeri.fi
                </h2>

                <p className={footerMutedTextClass}>
                  Buukkeri on Spiderlegs Productionsin kehittämä, mutta tästä
                  sivustosta eriytetty oma palvelu esiintyjien ja
                  tapahtumapalveluiden löytämiseen.
                </p>

                <div className="mt-5 space-y-3">
                  <a
                    href="https://buukkeri.fi"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="
                      inline-flex items-center gap-2 font-customFont1 font-extrabold
                      !text-[#111827] transition hover:!text-primary hover:underline
                      focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-white
                      dark:!text-white dark:hover:!text-primary dark:focus-visible:ring-offset-[#111114]
                    "
                    aria-label="Siirry Buukkeri-palveluun"
                    onClick={() =>
                      trackFooterEvent("ulkoinen_palvelu_klikattu", {
                        palvelu: "buukkeri",
                      })
                    }
                  >
                    Siirry Buukkeriin
                    <MdArrowOutward size={18} />
                  </a>

                  <br />

                  <a
                    href="https://buukkeri.fi/blogi"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="
                      inline-flex items-center gap-2 font-customFont1 font-semibold
                      !text-[#374151] transition hover:!text-primary hover:underline
                      focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-white
                      dark:!text-[#D1D5DB] dark:hover:!text-primary dark:focus-visible:ring-offset-[#111114]
                    "
                    aria-label="Siirry Buukkerin blogiin"
                    onClick={() =>
                      trackFooterEvent("ulkoinen_blogi_klikattu", {
                        blogi: "buukkeri",
                      })
                    }
                  >
                    Lue Buukkerin blogia
                    <MdArrowOutward size={16} />
                  </a>
                </div>
              </section>
            </div>

            {/* Alaosa */}
            <div
              className="
                flex flex-col gap-3 border-t border-[#E5E7EB] pt-6 text-center
                dark:border-white/10
                md:flex-row md:items-center md:justify-between md:text-left
              "
            >
              <p className="font-customFont1 text-sm font-semibold !text-[#4B5563] dark:!text-[#D1D5DB]">
                © {new Date().getFullYear()} Spiderlegs Productions. Kaikki
                oikeudet pidätetään.
              </p>

              <a
                href="https://arttustenius.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="
                  font-customFont1 text-sm font-extrabold
                  !text-[#111827] transition hover:!text-primary hover:underline
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-white
                  dark:!text-white dark:hover:!text-primary dark:focus-visible:ring-offset-[#111114]
                "
                onClick={() =>
                  trackFooterEvent("tekijalinkki_klikattu", {
                    kohde: "arttustenius.com",
                  })
                }
              >
                Toteutus: Arttu Stenius
              </a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}